import { Badge } from '@safetyhub/ui';
import type { ParticipantState } from '../../hooks/useParticipantsState';
import type { AssignmentType } from '../../lib/organizations';
import { withObjectParticle } from '@safetyhub/shared';

interface ParticipantSelectorProps {
  participants: ParticipantState[];
  onToggle: (memberId: string) => void;
  onSetPrimary: (memberId: string) => void;
  disabled?: boolean;
  /** 새 담당자 추가 버튼 클릭 핸들러 (없으면 버튼 숨김) */
  onAddMember?: () => void;
  /** 배정된 멤버 없을 때 표시 문구 */
  emptyMessage?: string;
}

/**
 * 참가자 체크박스 + PRIMARY 라디오 + 역할 뱃지 렌더링.
 * useParticipantsState 결과를 그대로 전달받아 화면에 표시.
 */
export function ParticipantSelector({
  participants,
  onToggle,
  onSetPrimary,
  disabled = false,
  onAddMember,
  emptyMessage = '이 현장에 배정된 안전관리자가 없습니다.',
}: ParticipantSelectorProps) {
  return (
    <div className="border border-line rounded-card overflow-hidden">
      {participants.length === 0 ? (
        <div className="px-3 py-3 text-center">
          <p className="text-sm text-ink-500">{emptyMessage}</p>
        </div>
      ) : (
        participants.map((p) => (
          <div
            key={p.memberId}
            className={`flex items-center gap-3 px-3 py-2.5 border-b border-line-soft last:border-b-0 ${
              p.checked ? 'bg-white' : 'bg-surface-soft'
            }`}
          >
            <input
              type="checkbox"
              aria-label={`${p.name} 참가 여부`}
              checked={p.checked}
              onChange={() => onToggle(p.memberId)}
              disabled={disabled || p.role === 'PRIMARY'}
              className="h-4 w-4 text-brand-600 border-ink-300 rounded focus:ring-brand-500 shrink-0"
            />
            <button
              type="button"
              onClick={() => onSetPrimary(p.memberId)}
              disabled={disabled || p.role === 'PRIMARY'}
              aria-label={
                p.role === 'PRIMARY'
                  ? `${p.name} — 정 담당자`
                  : `${withObjectParticle(p.name)} 정 담당자로 지정`
              }
              title={p.role === 'PRIMARY' ? '정 담당자' : '클릭하여 정 담당자로 지정'}
              className={`shrink-0 text-base leading-none w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
                p.role === 'PRIMARY'
                  ? 'text-yellow-500 cursor-default'
                  : 'text-ink-300 hover:text-yellow-400 cursor-pointer'
              }`}
            >
              ★
            </button>
            <div className="flex-1 min-w-0">
              <span
                className={`text-sm font-medium ${p.checked ? 'text-ink-900' : 'text-ink-400'}`}
              >
                {p.name}
              </span>
              <span className="ml-2 text-xs text-ink-400 truncate">{p.identifier}</span>
            </div>
            <AssignmentTypeBadge type={p.role} />
          </div>
        ))
      )}
      {onAddMember && (
        <div className="border-t border-dashed border-ink-200">
          <button
            type="button"
            onClick={onAddMember}
            disabled={disabled}
            className="w-full flex items-center gap-1.5 px-3 py-2 text-sm text-brand-600 hover:bg-brand-50 disabled:opacity-50"
          >
            <span className="text-base leading-none">+</span>
            <span>새 담당자 추가</span>
          </button>
        </div>
      )}
    </div>
  );
}

function AssignmentTypeBadge({ type }: { type?: AssignmentType }) {
  if (type === 'PRIMARY') {
    return (
      <Badge tone="brand" shape="rounded" className="shrink-0">
        정
      </Badge>
    );
  }
  if (type === 'SECONDARY') {
    return (
      <Badge tone="slate" shape="rounded" className="shrink-0">
        부
      </Badge>
    );
  }
  return null;
}
